import type { MatchState } from './state';
import type { SimConfig } from '../config/simConfig';
import type { SimEvent } from './events';
import type { ContactEvent } from '../physics/collide';
import { inReceptionGrace, shieldedFromBehind } from './possession';

// Punch resolution (§5.4/§5.6) — a PUNCH_ACTIVE player lands on the nearest opponent inside the punch sphere
// in front of them: the victim ragdolls and takes PUNCH_VICTIM_IMPULSE along the punch direction; a held
// ball drops live (possession release is handled by resolvePossession's no-touch rule). Emits PUNCH_LANDED +
// RAGDOLL, marks the puncher as having connected (short recovery), and returns a synthetic ContactEvent so
// the referee can judge the punch as a foul. Pure apart from the state it mutates.

export function resolveViolence(state: MatchState, cfg: SimConfig, events: SimEvent[]): ContactEvent[] {
  const punchContacts: ContactEvent[] = [];
  const reach = cfg.actions.PUNCH_REACH;
  const impulse = cfg.actions.PUNCH_VICTIM_IMPULSE;
  for (const p of state.players) {
    if (p.action !== 'PUNCH_ACTIVE' || p.sentOff || p.punchHit) continue; // one landing per punch
    const fx = Math.cos(p.facing);
    const fy = Math.sin(p.facing);
    let victim: (typeof state.players)[number] | undefined;
    let best = reach;
    for (const v of state.players) {
      if (v.team === p.team || v.sentOff || v.action === 'RAGDOLL') continue;
      const dx = v.pos.x - p.pos.x;
      const dy = v.pos.y - p.pos.y;
      const d = Math.hypot(dx, dy);
      if (d > best) continue;
      if (d > 1e-6 && (dx / d) * fx + (dy / d) * fy < 0.3) continue; // must be in front of the fist
      best = d;
      victim = v;
    }
    if (!victim) continue;
    const victimActionBefore = victim.action; // record for the synthetic contact BEFORE we ragdoll them
    // land it: ragdoll + impulse (a GK_HOLD holder loses the ball via resolvePossession's no-touch rule)
    victim.action = 'RAGDOLL';
    victim.actionTicks = 0;
    victim.charge = null;
    victim.vel.x += fx * impulse;
    victim.vel.y += fy * impulse;
    p.punchHit = true;
    state.discipline.perPlayer[p.id]!.punchesThrown += 1;
    events.push({ t: 'PUNCH_LANDED', puncher: p.id, victim: victim.id });
    events.push({ t: 'RAGDOLL', entity: victim.id, cause: 'PUNCH' });
    punchContacts.push({ a: p.id, b: victim.id, impulse, n: { x: fx, y: fy }, relSpeed: impulse, aAction: 'PUNCH_ACTIVE', bAction: victimActionBefore, positions: [{ ...p.pos }, { ...victim.pos }] });
  }
  return punchContacts;
}

/**
 * D40 legal shoulder barge: a SHOVE landed while running WITH the carrier (velocity-parallel ≥ PAR_DOT,
 * both at real speed) and contacting from the SIDE (|cos vs carrier facing| ≤ SIDE_DOT) staggers the
 * carrier (brief GETUP) and knocks the ball loose toward the barge direction — the fair barge. Frontal
 * and from-behind shoves keep their ordinary collision + referee treatment. One barge per tick.
 */
export function resolveBarge(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  const b = state.ball;
  if (b.possessor === null) return;
  const carrier = state.players[b.possessor];
  if (!carrier || carrier.sentOff || carrier.action === 'GK_HOLD') return;
  // D131: a fresh receiver is not barge-able. The barge has no claim to be barred, so it reads the
  // window directly — same rule as the body-block, and between them they are the two buttonless steals
  // the clip actually recorded (3 each, against 0 clean tackles).
  if (inReceptionGrace(b)) return;
  const cs = Math.hypot(carrier.vel.x, carrier.vel.y);
  if (cs < 2) return; // shoulder battles happen at speed
  const bg = cfg.actions.barge;
  for (const p of state.players) {
    if (p.team === carrier.team || p.sentOff || p.action !== 'SHOVE') continue;
    const dx = carrier.pos.x - p.pos.x;
    const dy = carrier.pos.y - p.pos.y;
    const d = Math.hypot(dx, dy);
    if (d > bg.REACH + cfg.collision.CAPSULE_R) continue;
    const ps = Math.hypot(p.vel.x, p.vel.y);
    if (ps < 2) continue;
    if ((p.vel.x * carrier.vel.x + p.vel.y * carrier.vel.y) / (ps * cs) < bg.PAR_DOT) continue; // not running with him
    const fx = Math.cos(carrier.facing);
    const fy = Math.sin(carrier.facing);
    const bearing = d > 1e-6 ? Math.abs(((-dx) / d) * fx + ((-dy) / d) * fy) : 1; // carrier → shover vs his facing
    if (bearing > bg.SIDE_DOT) continue; // not from the side
    // D131: …and never from inside the carrier's back cone. SIDE_DOT alone (|cos| ≤ 0.7) admits bearings
    // out to ~134°, so the "legal shoulder barge" band overlapped the D80 shield cone and the clip's
    // "they steal from far behind me" was, in part, this verb being legal there. Same predicate the
    // tackle and the trap poke use, so shielding means one thing across all three. From-behind contact
    // stays physically possible — it is now a foul candidate rather than a free steal.
    if (shieldedFromBehind(carrier.facing, carrier.pos, p.pos, cfg.possession.SHIELD_CONE_DOT)) continue;
    carrier.action = 'GETUP'; // staggered — brief input-ignored stumble
    carrier.actionTicks = Math.floor((cfg.actions.DUR.GETUP ?? 12) * 0.4);
    carrier.charge = null;
    b.possessor = null;
    const kx = d > 1e-6 ? dx / d : fx;
    const ky = d > 1e-6 ? dy / d : fy;
    b.vel.x += kx * bg.KNOCK;
    b.vel.y += ky * bg.KNOCK;
    b.claimCooldownTicks = 2;
    // D131 provenance fix: the cooldown names the SHOVER, not his victim. Barring the victim while
    // `lastTouch` also named him made the two fields agree, which is precisely the pairing D100 reads as
    // "a dribbler knocked this ball ahead" — so the shield cone was computed from the BEATEN player's
    // facing and used to refuse the side that had just won the ball. Naming the shover is also what the
    // verb already implies: SHOVE is NO_TOUCH (D120), so he could not gather it this tick regardless.
    b.claimCooldownOnly = p.id;
    b.kickedBy = null;
    b.lastTouch = { entity: carrier.id, team: carrier.team, tick: state.tick };
    events.push({ t: 'POSSESSION', entity: null, why: 'BARGE' });
    return;
  }
}
